import React, {useEffect, useState} from 'react';
import ReactDOM from 'react-dom';
import {useSubmitEventDraft} from './shared/useSubmitEventDraft';

const SubmitEventModal = ({isOpen, onClose, map, onExistingFound, labels}) => {
    const t = labels || {};
    const [step, setStep] = useState('input');
    const [inputValue, setInputValue] = useState('');
    const [isPicking, setIsPicking] = useState(false);
    const [toast, setToast] = useState(null);
    const form = useSubmitEventDraft({labels, onExistingFound});
    const {draft, error} = form;
    const parsing = form.status === 'parsing';
    const submitting = form.status === 'submitting';

    useEffect(() => {
        if (isOpen) {
            setStep('input');
            setInputValue('');
            form.reset();
            setIsPicking(false);
            setToast(null);
        }
    }, [isOpen]);

    useEffect(() => {
        if (!map || !isPicking) return undefined;
        const handler = (event) => {
            const {lat, lng} = event.latlng;
            form.setPoint(lat, lng);
            setIsPicking(false);
        };
        const cancelOnEscape = (event) => {
            if (event.key === 'Escape') setIsPicking(false);
        };
        map.on('click', handler);
        document.addEventListener('keydown', cancelOnEscape);
        return () => {
            map.off('click', handler);
            document.removeEventListener('keydown', cancelOnEscape);
        };
    }, [map, isPicking]);

    if (!isOpen) {
        return null;
    }

    const handleClose = () => {
        setIsPicking(false);
        onClose();
    };

    const handleNext = async () => {
        const result = await form.parseUrl(inputValue);
        if (result === 'existing') handleClose();
        if (result === 'parsed') setStep('details');
    };

    const handleSubmit = async () => {
        const result = await form.submit();
        if (result === 'existing') handleClose();
        if (result !== 'sent') return;
        setToast(t.submitSuccess);
        setTimeout(() => {
            setToast(null);
            handleClose();
        }, 2500);
    };

    const renderInputStep = () => (
        <div className="submit-modal__step">
            <h2 className="submit-modal__title">{t.submitTitle}</h2>
            <p className="submit-modal__hint">
                {t.submitHint}
            </p>
            <input
                className="submit-modal__input"
                type="url"
                value={inputValue}
                onChange={(event) => setInputValue(event.target.value)}
                placeholder="https://…"
                disabled={parsing}
                autoFocus
            />
            {error && <div className="submit-modal__error">{error}</div>}
            <div className="submit-modal__actions">
                <button
                    type="button"
                    className="submit-modal__btn submit-modal__btn--secondary"
                    onClick={handleClose}
                    disabled={parsing}
                >
                    {t.submitCancel}
                </button>
                <button
                    type="button"
                    className="submit-modal__btn submit-modal__btn--primary"
                    onClick={handleNext}
                    disabled={parsing}
                >
                    {parsing ? t.submitLoading : t.submitNext}
                </button>
            </div>
        </div>
    );

    const renderDetailsStep = () => (
        <div className="submit-modal__step">
            <h2 className="submit-modal__title">{t.submitDetailsTitle}</h2>
            <label className="submit-modal__label">{t.submitName}</label>
            <input
                className="submit-modal__input"
                type="text"
                value={draft.name}
                onChange={(event) => form.setField('name', event.target.value)}
            />
            {draft.description && (
                <p className="submit-modal__description">{draft.description}</p>
            )}
            <label className="submit-modal__label">{t.submitPlace}</label>
            <div className="submit-modal__location">
                {draft.latitude != null && draft.longitude != null ? (
                    <span>{draft.latitude.toFixed(5)}, {draft.longitude.toFixed(5)}</span>
                ) : (
                    <span className="submit-modal__placeholder">{t.submitPlaceEmpty}</span>
                )}
                <button
                    type="button"
                    className="submit-modal__btn-link"
                    onClick={() => setIsPicking(true)}
                >
                    {draft.latitude != null ? t.submitPlaceChange : t.submitPlacePick}
                </button>
            </div>
            <label className="submit-modal__label">{t.submitStart}</label>
            <input
                className="submit-modal__input"
                type="datetime-local"
                value={draft.startAt}
                onChange={(event) => form.setField('startAt', event.target.value)}
            />
            <label className="submit-modal__label">{t.submitEnd}</label>
            <input
                className="submit-modal__input"
                type="datetime-local"
                value={draft.endAt}
                onChange={(event) => form.setField('endAt', event.target.value)}
            />
            {error && <div className="submit-modal__error">{error}</div>}
            <div className="submit-modal__actions">
                <button
                    type="button"
                    className="submit-modal__btn submit-modal__btn--secondary"
                    onClick={() => setStep('input')}
                    disabled={submitting}
                >
                    {t.submitBack}
                </button>
                <button
                    type="button"
                    className="submit-modal__btn submit-modal__btn--primary"
                    onClick={handleSubmit}
                    disabled={submitting}
                >
                    {submitting ? t.submitSending : t.submitSend}
                </button>
            </div>
        </div>
    );

    if (isPicking) {
        return ReactDOM.createPortal(
            <div className="submit-pick-banner" role="status">
                <span>{t.submitPickOnMap}</span>
                <button
                    type="button"
                    className="submit-pick-banner__cancel"
                    onClick={() => setIsPicking(false)}
                >
                    {t.submitCancel}
                </button>
            </div>,
            document.body
        );
    }

    return ReactDOM.createPortal(
        <div className="submit-modal-overlay" role="dialog" aria-modal="true">
            <div className="submit-modal">
                {step === 'input' ? renderInputStep() : renderDetailsStep()}
            </div>
            {toast && <div className="submit-toast">{toast}</div>}
        </div>,
        document.body
    );
};

export default SubmitEventModal;
